import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Paper,
  Chip,
  Card,
  CardContent,
  Avatar,
  Stack,
  Divider,
} from "@mui/material";
import {
  Search,
  TrendingUp,
  NewReleases,
  Whatshot,
  Create,
  ArrowForward,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import PostList from "../components/Blog/PostList";
import { useAuth } from "../services/auth";

const Home = () => {
  const { isAuthenticated } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          py: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box textAlign="center" maxWidth="800px" mx="auto">
            <Typography
              variant="h2"
              component="h1"
              fontWeight="700"
              gutterBottom
              sx={{
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                mb: 2,
              }}
            >
              Welcome to Our Blog
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                opacity: 0.95,
                fontWeight: 400,
                fontSize: { xs: "1rem", md: "1.25rem" },
              }}
            >
              Discover stories, thinking, and expertise from writers on any
              topic
            </Typography>
            {!isAuthenticated && (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  component={RouterLink}
                  to="/signup"
                  sx={{
                    bgcolor: "white",
                    color: "primary.main",
                    px: 4,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.9)",
                      transform: "translateY(-2px)",
                      boxShadow: 3,
                    },
                    transition: "all 0.3s ease",
                  }}
                  endIcon={<ArrowForward />}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={RouterLink}
                  to="/login"
                  sx={{
                    borderColor: "white",
                    color: "white",
                    px: 4,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Sign In
                </Button>
              </Stack>
            )}
          </Box>
        </Container>
      </Box>

      {/* Stats Section */}

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 8, pb: 8 }}>
        {/* Search Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            bgcolor: "background.paper",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <TextField
            fullWidth
            placeholder="Search posts by title, author, or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </Paper>

        {/* Header with Create Button */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Box>
            <Typography
              variant="h4"
              component="h2"
              fontWeight="700"
              gutterBottom
              sx={{ color: "text.primary" }}
            >
              Latest Posts
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Explore the most recent articles from our community
            </Typography>
          </Box>
          {isAuthenticated && (
            <Button
              variant="contained"
              component={RouterLink}
              to="/create-post"
              startIcon={<Create />}
              size="large"
              sx={{
                px: 3,
                py: 1.5,
                fontWeight: 600,
                boxShadow: 2,
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 4,
                },
                transition: "all 0.3s ease",
              }}
            >
              Create New Post
            </Button>
          )}
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Posts List */}
        <PostList searchTerm={searchTerm} />
      </Container>
    </Box>
  );
};

export default Home;
